import React from "react";
import "./ReadonlyToothChart.css";
import { teethPaths } from "../../shared/toothchart/teethData";

interface ReadonlyToothChartProps {
  teeth: number[];
  states?: Record<number, string>;
  overlays?: { crown?: number[]; rct?: number[] };
}

export default function ReadonlyToothChart({
  teeth,
  states,
  overlays
}: ReadonlyToothChartProps) {
  const teethSet = new Set(teeth);
  const stateMap = states ?? {};
  const crownSet = new Set(overlays?.crown ?? []);
  const rctSet = new Set(overlays?.rct ?? []);

  const resolveStateClass = (state?: string) => {
    switch (state) {
      case "RCT_Open":
        return "rct-open";
      case "RCT_Done":
        return "rct-done";
      case "Filling":
        return "filling";
      case "CROWN_CUTTING":
      case "Crown_Cutting":
      case "CrownCutting":
      case "Abutment_Prep":
      case "AbutmentPrep":
        return "crown-cutting";
      case "Crown":
        return "crown";
      case "Veneer":
        return "veneer";
      case "Implant":
        return "implant";
      case "Missing":
        return "missing";
      default:
        return "";
    }
  };

  const getToothClassName = (toothNumber: number) => {
    const classes = ["tooth-group"];

    if (teethSet.has(toothNumber)) {
      classes.push("selected");
    }

    const visualState = stateMap[toothNumber];
    const stateClass = resolveStateClass(visualState);
    if (stateClass) {
      classes.push(stateClass);
    }

    if (crownSet.has(toothNumber)) {
      classes.push("crown");
    }

    if (rctSet.has(toothNumber)) {
      classes.push("rct-done");
    }

    return classes.join(" ");
  };

  const QUADRANTS = [
    { id: "UR", label: "Upper Right (11-18)", prefix: 1, transform: "" },
    { id: "UL", label: "Upper Left (21-28)", prefix: 2, transform: "scale(-1, 1) translate(-410, 0)" },
    { id: "LR", label: "Lower Right (41-48)", prefix: 4, transform: "scale(1, -1) translate(0, -660)" },
    { id: "LL", label: "Lower Left (31-38)", prefix: 3, transform: "scale(-1, -1) translate(-410, -660)" }
  ];

  return (
    <div className="svg-chart-container">
      <svg
        viewBox="0 80 410 580"
        className="dental-chart-svg"
        role="img"
        aria-label="Dental Chart"
      >
        {QUADRANTS.map((quad) => (
          <g key={quad.id} transform={quad.transform} role="group" aria-label={quad.label}>
            {teethPaths.map((tooth) => {
              const toothNumber = Number(`${quad.prefix}${tooth.name}`);

              return (
                <g
                  key={toothNumber}
                  className={getToothClassName(toothNumber)}
                  role="img"
                  aria-label={`Tooth ${toothNumber} ${tooth.type}`}
                >
                  <path
                    d={tooth.outlinePath}
                    className="tooth-outline"
                  />

                  <path
                    d={tooth.shadowPath}
                    className="tooth-shadow"
                  />

                  {Array.isArray(tooth.lineHighlightPath) ? (
                    tooth.lineHighlightPath.map((d, i) => (
                      <path key={i} d={d} className="tooth-highlight" />
                    ))
                  ) : (
                    <path d={tooth.lineHighlightPath as string} className="tooth-highlight" />
                  )}
                </g>
              );
            })}
          </g>
        ))}

        <g id="chartLabels" style={{ pointerEvents: "none", userSelect: "none" }}>
          <text x="30" y="320" className="chart-label">UR</text>
          <text x="350" y="320" className="chart-label">UL</text>
          <text x="350" y="520" className="chart-label">LL</text>
          <text x="30" y="520" className="chart-label">LR</text>
        </g>
      </svg>

      <div className="chart-legend">
        <div className="legend-item"><span className="dot filling"></span> Filling</div>
        <div className="legend-item"><span className="dot rct-done"></span> RCT (Body)</div>
        <div className="legend-item"><span className="dot crown-cutting"></span> Abutment Prep</div>
        <div className="legend-item"><span className="dot crown"></span> Crown (Border)</div>
        <div className="legend-item"><span className="dot missing"></span> Missing</div>
        <div className="legend-item"><span className="dot selected"></span> Selected</div>
      </div>
    </div>
  );
}
